import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PhoneInputWithCode } from "@/components/PhoneInputWithCode";
import axios from "axios";
import { Briefcase, Check, Loader2, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function EmploymentVerificationPortal() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [data, setData] = useState(null);

  const [verifierInfo, setVerifierInfo] = useState({
    org_name: "", org_city: "", org_state: "", org_zip: "",
    job_title: "",
    first_name: "", last_name: "",
    email: "", phone: "", phone_country_code: "+1|US",
    additional_phone: "", additional_phone_country_code: "+1|US",
  });
  const [startDate, setStartDate] = useState("");
  const [stillEmployed, setStillEmployed] = useState("");
  const [workedMinHours, setWorkedMinHours] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/employment-verify/${token}`);
        if (res.data.completed) {
          setCompleted(true);
          setSuccessMsg(res.data.message);
        } else {
          setData(res.data);
          const v = res.data.verification;
          setVerifierInfo(prev => ({
            ...prev,
            org_name: v.employer_name || "",
            org_city: v.employer_city || "",
            org_state: v.employer_state || "",
            org_zip: v.employer_zip || "",
            job_title: v.job_title || "",
            first_name: v.verifier_first_name || "",
            last_name: v.verifier_last_name || "",
            email: v.verifier_email || "",
            phone: v.verifier_phone || "",
          }));
          setStartDate(v.start_date || "");
        }
      } catch (err) {
        setError(err.response?.data?.detail || "This link is invalid or has expired.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const handleSubmit = async () => {
    if (!stillEmployed || !workedMinHours) {
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/employment-verify/${token}/submit`, {
        verifier_info: verifierInfo,
        start_date: startDate,
        still_employed: stillEmployed,
        worked_min_hours: workedMinHours,
      });
      setCompleted(true);
      setSuccessMsg(res.data.message);
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e14] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF9800]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0e14] flex items-center justify-center p-4">
        <div className="max-w-md w-full rounded-2xl border border-red-500/20 bg-red-500/5 p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-white text-xl font-semibold mb-2">Link Invalid</h2>
          <p className="text-slate-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen bg-[#0a0e14] flex items-center justify-center p-4">
        <div className="max-w-md w-full rounded-2xl border border-[#739600]/30 bg-[#739600]/5 p-8 text-center">
          <Check className="w-12 h-12 text-[#739600] mx-auto mb-4" />
          <h2 className="text-white text-xl font-semibold mb-2">Thank You!</h2>
          <p className="text-slate-400 text-sm">{successMsg}</p>
        </div>
      </div>
    );
  }

  const inputCls = "h-11 bg-black/30 border-white/[0.08] text-white rounded-xl px-4 focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 placeholder:text-slate-600";
  const labelCls = "text-sm text-slate-300 mb-1.5 block";

  return (
    <div className="min-h-screen bg-[#0a0e14] py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">

        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl border border-white/[0.06]">
          <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35]/20 via-transparent to-[#FF9800]/10" />
          <div className="relative p-8 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF6B35]/20 border border-[#FF6B35]/30 mb-4">
              <Briefcase className="w-3.5 h-3.5 text-[#FF9800]" />
              <span className="text-[#FF9800] text-xs font-medium tracking-wide uppercase">University of St. Augustine</span>
            </div>
            <h1 className="text-white text-2xl font-semibold mb-3">Verification of Employment</h1>
            <p className="text-slate-300 text-sm leading-relaxed max-w-xl mx-auto">
              The person listed below is applying for admission to the University of St. Augustine for Health Sciences. In order to be admitted, applicants must offer verification that they were employed as an Occupational Therapy Assistant in a clinical setting. You have been identified as someone who can offer such confirmation for this applicant.
            </p>
            <p className="text-slate-300 text-sm leading-relaxed max-w-xl mx-auto mt-3">
              If you are willing, please submit this short electronic form <strong className="text-white">verifying the period of employment and the position the applicant held in your organization or practice</strong>. Your prompt attention to this matter is a critical piece in the student's application process, and in our team's ability to render a timely admissions decision.
            </p>
            <p className="text-slate-400 text-xs mt-4 italic">
              Sincerely, Department of Admissions — University of St. Augustine for Health Sciences
            </p>
          </div>
        </div>

        {/* Applicant Info */}
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6">
          <h3 className="text-white text-sm font-semibold mb-4 uppercase tracking-wider">Applicant Information</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-500">Applicant who requested verification of employment</span>
              <p className="text-white mt-1">{data.applicant_name}</p>
            </div>
            <div>
              <span className="text-slate-500">who is applying to the program</span>
              <p className="text-white mt-1">{data.program}</p>
            </div>
          </div>
        </div>

        {/* Employment Verifier Information */}
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 space-y-5">
          <h3 className="text-white text-sm font-semibold uppercase tracking-wider">Employment Verifier Information</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className={labelCls}>Organization / Practice Name</Label>
              <Input value={verifierInfo.org_name} onChange={(e) => setVerifierInfo({ ...verifierInfo, org_name: e.target.value })} className={inputCls} data-testid="emp-portal-org" />
            </div>
            <div>
              <Label className={labelCls}>Organization / Practice City</Label>
              <Input value={verifierInfo.org_city} onChange={(e) => setVerifierInfo({ ...verifierInfo, org_city: e.target.value })} className={inputCls} data-testid="emp-portal-city" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className={labelCls}>Organization / Practice State</Label>
              <Input value={verifierInfo.org_state} onChange={(e) => setVerifierInfo({ ...verifierInfo, org_state: e.target.value })} className={inputCls} data-testid="emp-portal-state" />
            </div>
            <div>
              <Label className={labelCls}>Zip Code <span className="text-red-400">*</span></Label>
              <Input value={verifierInfo.org_zip} onChange={(e) => setVerifierInfo({ ...verifierInfo, org_zip: e.target.value })} className={inputCls} data-testid="emp-portal-zip" />
            </div>
          </div>

          <div>
            <Label className={labelCls}>Job Title <span className="text-red-400">*</span></Label>
            <Input value={verifierInfo.job_title} onChange={(e) => setVerifierInfo({ ...verifierInfo, job_title: e.target.value })} className={inputCls} data-testid="emp-portal-title" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className={labelCls}>First Name</Label>
              <Input value={verifierInfo.first_name} onChange={(e) => setVerifierInfo({ ...verifierInfo, first_name: e.target.value })} className={inputCls} data-testid="emp-portal-first" />
            </div>
            <div>
              <Label className={labelCls}>Last Name</Label>
              <Input value={verifierInfo.last_name} onChange={(e) => setVerifierInfo({ ...verifierInfo, last_name: e.target.value })} className={inputCls} data-testid="emp-portal-last" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className={labelCls}>Email</Label>
              <Input value={verifierInfo.email} onChange={(e) => setVerifierInfo({ ...verifierInfo, email: e.target.value })} className={inputCls} data-testid="emp-portal-email" />
            </div>
            <div>
              <Label className={labelCls}>Phone</Label>
              <PhoneInputWithCode
                value={verifierInfo.phone}
                countryCode={verifierInfo.phone_country_code}
                onPhoneChange={(val) => setVerifierInfo({ ...verifierInfo, phone: val })}
                onCodeChange={(val) => setVerifierInfo({ ...verifierInfo, phone_country_code: val })}
                testId="emp-portal-phone"
                theme="dark"
              />
            </div>
          </div>

          <div>
            <Label className={labelCls}>Additional Phone</Label>
            <div className="max-w-xs">
              <PhoneInputWithCode
                value={verifierInfo.additional_phone}
                countryCode={verifierInfo.additional_phone_country_code}
                onPhoneChange={(val) => setVerifierInfo({ ...verifierInfo, additional_phone: val })}
                onCodeChange={(val) => setVerifierInfo({ ...verifierInfo, additional_phone_country_code: val })}
                testId="emp-portal-addl-phone"
                theme="dark"
              />
            </div>
          </div>
        </div>

        {/* Details and Confirmation */}
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 space-y-5">
          <h3 className="text-white text-sm font-semibold uppercase tracking-wider">Details and Confirmation</h3>

          <div>
            <Label className={labelCls}>Date Employment Started</Label>
            <Input value={startDate} onChange={(e) => setStartDate(e.target.value)} placeholder="MM/DD/YYYY" className={`${inputCls} max-w-xs`} data-testid="emp-portal-start" />
          </div>

          <div>
            <Label className={labelCls}>Is applicant still employed? <span className="text-red-400">*</span></Label>
            <Select value={stillEmployed} onValueChange={setStillEmployed}>
              <SelectTrigger className="h-11 bg-black/30 border-white/[0.08] text-white rounded-xl max-w-xs" data-testid="emp-portal-still-employed">
                <SelectValue placeholder="Please select..." />
              </SelectTrigger>
              <SelectContent className="bg-[#0f1419] border-white/10">
                <SelectItem value="yes" className="text-slate-300">Yes</SelectItem>
                <SelectItem value="no" className="text-slate-300">No</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className={labelCls}>
              Did this employee work at least 2,080 hours (1-year full-time equivalent) in your employment?* (Yes/No) <span className="text-red-400">*</span>
            </Label>
            <Select value={workedMinHours} onValueChange={setWorkedMinHours}>
              <SelectTrigger className="h-11 bg-black/30 border-white/[0.08] text-white rounded-xl max-w-xs" data-testid="emp-portal-min-hours">
                <SelectValue placeholder="Please select..." />
              </SelectTrigger>
              <SelectContent className="bg-[#0f1419] border-white/10">
                <SelectItem value="yes" className="text-slate-300">Yes</SelectItem>
                <SelectItem value="no" className="text-slate-300">No</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="p-4 rounded-xl bg-slate-800/50 border border-white/[0.06] text-slate-400 text-xs leading-relaxed">
            By clicking <strong className="text-white">Submit</strong>, I acknowledge that the information provided is accurate and truthful.
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end gap-3 pb-8">
          <Button
            variant="outline"
            className="border-white/20 text-white hover:bg-white/5 rounded-xl px-6"
            onClick={() => window.close()}
            data-testid="emp-portal-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !stillEmployed || !workedMinHours}
            className="bg-[#FF6B35] hover:bg-[#e55a25] text-white rounded-xl px-6 shadow-[0_0_15px_rgba(255,107,53,0.3)]"
            data-testid="emp-portal-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Submit
          </Button>
        </div>
      </div>
    </div>
  );
}
